import callApi, { BaseResponse, BaseResponseDto, BaseResponsePagedDto } from "./baseRequest"

interface GetTokenRequest {
    email: string,
    password: string,
    token: string
}

interface GetTokenResponse extends BaseResponse {
    token: string,
    userId: string,
    customers: any[],
    recaptcha: boolean
}

interface GetTokenAccessAsResponse {
    token: string,
    userId: string,
    customers: any[],
    backofficeEmail: string
}

interface GetRecaptchaRequest {
    token: string,
}

interface GetRecaptchaResponse {
    passed: boolean,
    message: string
}

interface PostValidationRegister {
    name: string;
    document: string;
    email: string;
    phone: string;
    resendEmail: boolean;
}

export enum RequestType {
    Register = 1,
    ResetPassword = 2,
    FirstAccess = 3,
}

export enum Profile {
    Read = 1,
    LogOperator = 2,
    Operator = 3,
    Adm = 4
}

export interface UsersResponse {
    email: string,
    name: string,
    createdAt: Date,
    firstAcessAt: Date,
    lastAcessAt: Date,
    active: boolean,
    profile: Profile,
    id: string,
}

export interface CustomerResponse {
    customerId: string,
    issuer: string,
    customerName: string,
    cpfCnpj: string,
    userId: string,
    country: string
}

export interface AllCustomerDto {
    id: string;
    issuer: string;
    issuerName: string;
    country: string;
    city: string;
    postalCode: string;
    region: string;
    address: string;
    neighborhood: string;
    cnpjCpf: string;
    email: string;
    taxDomicile: string;
    accountsGroup: string;
    toBeDeleted: string;
    searchTerm1 : string;
    searchTerm2 : string;
}

interface UsersByCustomerResponse {
    users: UsersResponse[]
}

interface CustomerByUserResponse {
    customers: CustomerResponse[]
}

export interface UserBackOfficeDto {
    id: string
    email: string
    active: boolean
    profile: number
    country: string
  }
interface AllCustomerResponse {
    customers: AllCustomerDto[]
}

export interface UserCustomerDto {
    userId: string
    name: string
    email: string
    active: boolean
    profile: number
    customers: CustomerDto[]
  }

export interface CustomerDto {
    customerId: string
    cnpjCpf: string
    issuer: string
    issuerName: string,
    searchTerm1: string,
    searchTerm2: string
  }

export function getProfileByStr(profile: string) {

    switch (Number(profile)) {
        case Profile.Adm:
            return Profile.Adm;
        case Profile.Read:
            return Profile.Read;
        case Profile.LogOperator:
            return Profile.LogOperator;
        case Profile.Operator:
            return Profile.Operator;
        default:
            return Profile.Read;
    }
}

export function getProfileToStr(profile: string) {

    switch (Number(profile)) {
        case Profile.Adm:
            return 'Admin';
        case Profile.Read:
            return 'Visualizador';
        case Profile.LogOperator:
            return 'Operador Log';
        case Profile.Operator:
            return 'Operador';
        default:
            return 'Visualizador';
    }
}

export function getProfileBackofficeToStr(profile: string) {

    switch (Number(profile)) {
        case 1:
            return 'Admin';
        case 3:
            return 'Vendedor';
        case 2:
            return 'Operador';
        default:
            return 'Visualizador';
    }
}

const route = 'user';
const routeBackoffice = 'user/user-backoffice';

export function getToken(request: GetTokenRequest): Promise<GetTokenResponse> {
    const result: GetTokenResponse = {
        error: '',
        success: false,
        token: '',
        userId: '',
        customers: [],
        recaptcha: false
    };

    const response = callApi('POST', `${route}/token`, request)
        .then(response => {
            result.success = true;
            result.token = response?.data?.token;
            result.userId = response?.data?.userId;
            result.customers = response?.data?.customers;

            return result;
        }).catch(err => {
            result.error = err?.error;
            result.recaptcha = err?.recaptcha;

            return result;
        });

    return response;
}

export function getRecaptcha(request: GetRecaptchaRequest): Promise<BaseResponseDto<GetRecaptchaResponse>> {
    const result: BaseResponseDto<GetRecaptchaResponse> = {
        error: '',
        success: false,
        data: { passed: false, message: '' }
    };

    const response = callApi('POST', `${route}/validate-recaptcha`, request)
        .then(response => {
            result.success = true;
            result.data = response.data;


            return result;
        }).catch(err => {
            result.error = err.error;
            result.data = err.data;

            return result;
        });

    return response;
}

export function sendRegisterRequest(request: PostValidationRegister): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('POST', `${route}/validation-register`, request)
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function validateForgotPassEmail(email: string, requestType: RequestType = RequestType.ResetPassword): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('POST', `${route}/request-reset-pssw`, { email: email, requestType: requestType })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function validateForgotPassCode(email: string, code: string, requestType: RequestType = RequestType.ResetPassword): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('POST', `${route}/validate-code-reset-pssw`, { email: email, code: code, requestType: requestType })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function sendNewPass(email: string, code: string, newPass: string): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('POST', `${route}/reset-pssw`, { email: email, code: code, newPassword: newPass })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function getUsersByCustomer(issuer: string): Promise<BaseResponseDto<UsersByCustomerResponse>> {
    const result: BaseResponseDto<UsersByCustomerResponse> = {
        error: '',
        success: false,
        data: {
            users: []
        }
    };

    const response = callApi('GET', `${route}/user-by-customer-backoffice/${issuer}`)
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function getCustomersByUser(userId: string): Promise<BaseResponseDto<CustomerByUserResponse>> {
    const result: BaseResponseDto<CustomerByUserResponse> = {
        error: '',
        success: false,
        data: {
            customers: []
        }
    };

    const response = callApi('GET', `${route}/customers-by-user-backoffice/${userId}`)
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function updateUser(userId: string, profile: Number, active: boolean, customers: CustomerResponse[]): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('PUT', `${route}/backoffice/${userId}`, {
        profile,
        active,
        customers
    })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.success = true;
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function createUser(name: string, email: string, profile: Number, customerId: string[]): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('POST', `${route}/invite-new-user`, {
        customerId,
        name,
        email,
        profile
    })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.success = true;
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function getUsersCustomer(take: number,
    skip: number,
    search: string,
    status: string): Promise<BaseResponsePagedDto<UserCustomerDto[]>> {
    const result: BaseResponsePagedDto<UserCustomerDto[]> = {
        error: '',
        data: [],
        success: false,
        currentTake: take,
        totalItems: 0,
        totalPages: 0
    };

    const response = callApi('GET', `${route}/user-customer/?take=${take}&skip=${skip}&search=${search}&status=${status}`)
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
                result.totalItems = response.totalItems
                result.totalPages = response.totalPages
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function getAllCustomers(): Promise<BaseResponseDto<AllCustomerResponse>> {
    const result: BaseResponseDto<AllCustomerResponse> = {
        error: '',
        success: false,
        data: {
            customers: []
        }
    };

    const response = callApi('GET', `customer/get-all-customer`)
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function updateUserBackoffice(userId: string, profile: Number, active: boolean, country: string, email: string): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('PUT', `${routeBackoffice}/${userId}`, {
        profile,
        active,
        country,
        email
    })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.success = true;
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function getUsersBackoffice(
    take: number,
    skip: number,
    search: string): Promise<BaseResponsePagedDto<UserBackOfficeDto[]>> {
    const result: BaseResponsePagedDto<UserBackOfficeDto[]> = {
        error: '',
        data: [],
        success: false,
        currentTake: take,
        totalItems: 0,
        totalPages: 0
    };

    const response = callApi('GET', `${routeBackoffice}?take=${take}&skip=${skip}&search=${search}`)
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
                result.totalItems = response.totalItems
                result.totalPages = response.totalPages
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function createUserBackoffice(profile: Number, active: boolean, country: string, email: string): Promise<BaseResponseDto<any>> {
    const result: BaseResponseDto<any> = {
        error: '',
        success: false,
        data: null
    };

    const response = callApi('POST', `${routeBackoffice}`, {
        profile,
        active,
        country,
        email
    })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.success = true;
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function getUserDataBackoffice(email: string): Promise<BaseResponseDto<UserBackOfficeDto>> {
    const result: BaseResponseDto<UserBackOfficeDto> = {
        error: '',
        data: {
            active: true,
            country: `BR`,
            email: ``,
            id: ``,
            profile: 1
        },
        success: false,
    };

    const response = callApi('GET', `user/valid-user-backoffice/${email}`)
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}

export function getAccessAsToken(issuer: string, email: string): Promise<BaseResponseDto<GetTokenAccessAsResponse>> {
    const result: BaseResponseDto<GetTokenAccessAsResponse> = {
        error: '',
        data: {
            backofficeEmail: '',
            customers: [],
            token : '',
            userId : ''
        },
        success: false,
    };

    const response = callApi('POST', `user/access-as-token/${issuer}/${email}`, {
        email: `acessarcomo@beesby.com`,
        password: `${process.env.REACT_APP_ACCESS_SCRET}`
    })
        .then(response => {
            result.success = true;

            if (response.data) {
                result.data = response.data
            }
            return result;
        }).catch(err => {
            result.error = err.error;

            return result;
        });

    return response;
}
